import React, { useEffect, useMemo, useState } from "react";
import { useAuthFetch } from "../hooks/useAuthFetch";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../stili/StatistichePresenze.css";
import "../stili/BottoniFinali.css";

const API_URL = process.env.REACT_APP_API_URL;

export default function StatistichePresenzeForm() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [categorie, setCategorie] = useState([]);

  const [spCategoriaFiltro, setSpCategoriaFiltro] = useState("");
  const [spDataInizio, setSpDataInizio] = useState("");
  const [spDataFine, setSpDataFine] = useState("");

  const [spPresenzeStats, setSpPresenzeStats] = useState([]);
  const [spLoadingPresenze, setSpLoadingPresenze] = useState(false);
  const [spErrorPresenze, setSpErrorPresenze] = useState("");
  const [spMessage, setSpMessage] = useState("");

  useEffect(() => {
    authFetch(`${API_URL}/api/admin/categorie`)
      .then((res) =>
        res.ok ? res.json() : Promise.reject("Errore caricamento categorie")
      )
      .then((data) => {
        setCategorie(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error(err);
        setSpErrorPresenze("Impossibile caricare le categorie.");
        toast.error("Errore caricamento categorie");
      });
  }, [authFetch]);

  useEffect(() => {
    if (!spCategoriaFiltro || !spDataInizio || !spDataFine) {
      setSpPresenzeStats([]);
      setSpErrorPresenze("");
      setSpMessage("");
      return;
    }

    if (spDataFine < spDataInizio) {
      setSpPresenzeStats([]);
      setSpErrorPresenze("La data fine non può essere precedente alla data inizio.");
      return;
    }

    setSpLoadingPresenze(true);
    setSpErrorPresenze("");
    setSpMessage("");

    const params = new URLSearchParams({
      categoria: spCategoriaFiltro,
      dataInizio: spDataInizio,
      dataFine: spDataFine,
    });

    authFetch(
      `${API_URL}/api/admin/presenze/statistiche-presenze?${params.toString()}`
    )
      .then((res) =>
        res.ok ? res.json() : Promise.reject("Errore caricamento presenze")
      )
      .then((data) => {
        const lista = Array.isArray(data) ? data : [];
        setSpPresenzeStats(lista);
        setSpMessage(
          lista.length > 0
            ? `Trovati ${lista.length} giocatori nel periodo selezionato.`
            : "Nessuna presenza disponibile per i filtri selezionati."
        );
        setSpLoadingPresenze(false);
      })
      .catch((err) => {
        console.error(err);
        setSpErrorPresenze("Errore durante il caricamento delle statistiche presenze.");
        setSpLoadingPresenze(false);
      });
  }, [spCategoriaFiltro, spDataInizio, spDataFine, authFetch]);

  const handleReset = () => {
    setSpCategoriaFiltro("");
    setSpDataInizio("");
    setSpDataFine("");
    setSpPresenzeStats([]);
    setSpErrorPresenze("");
    setSpMessage("");
  };

  const riepilogo = useMemo(() => {
    const totale = spPresenzeStats.length;

    const media =
      totale > 0
        ? (
            spPresenzeStats.reduce(
              (acc, curr) => acc + Number(curr.percentualePresenza || 0),
              0
            ) / totale
          ).toFixed(2)
        : "0.00";

    const sopra80 = spPresenzeStats.filter(
      (g) => Number(g.percentualePresenza || 0) >= 80
    ).length;

    const sotto50 = spPresenzeStats.filter(
      (g) => Number(g.percentualePresenza || 0) < 50
    ).length;

    const migliore =
      totale > 0
        ? [...spPresenzeStats].sort(
            (a, b) =>
              Number(b.percentualePresenza || 0) -
              Number(a.percentualePresenza || 0)
          )[0]
        : null;

    return {
      totale,
      media,
      sopra80,
      sotto50,
      migliore,
    };
  }, [spPresenzeStats]);

  const categoriaLabel = useMemo(() => {
    return spCategoriaFiltro || "Nessuna categoria selezionata";
  }, [spCategoriaFiltro]);

  return (
    <div className="sp-page" translate="no">
      <div className="sp-shell">
        <div className="sp-container">
          <div className="sp-header">
            <h1>Report Presenze Giocatori</h1>
            <p>Controlla la percentuale di presenza per categoria e periodo.</p>
          </div>

          <div className="sp-card sp-filtri-card">
            <h2>Filtri</h2>

            <div className="sp-filtri-grid">
              <div className="sp-field">
                <label className="sp-label">Categoria</label>
                <select
                  className="sp-select"
                  value={spCategoriaFiltro}
                  onChange={(e) => setSpCategoriaFiltro(e.target.value)}
                >
                  <option value="">Seleziona una categoria</option>
                  {categorie.map((cat) => (
                    <option key={cat.id} value={cat.nome}>
                      {cat.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sp-field">
                <label className="sp-label">Data inizio</label>
                <input
                  className="sp-input"
                  type="date"
                  value={spDataInizio}
                  onChange={(e) => setSpDataInizio(e.target.value)}
                />
              </div>

              <div className="sp-field">
                <label className="sp-label">Data fine</label>
                <input
                  className="sp-input"
                  type="date"
                  value={spDataFine}
                  onChange={(e) => setSpDataFine(e.target.value)}
                  min={spDataInizio}
                />
              </div>
            </div>

            <div className="sp-actions">
              <button type="button" className="sp-btn sp-btn-secondary" onClick={handleReset}>
                Pulisci filtri
              </button>
            </div>
          </div>

          {(spErrorPresenze || spMessage) && (
            <div className={`sp-feedback ${spErrorPresenze ? "error" : "success"}`}>
              {spErrorPresenze || spMessage}
            </div>
          )}

          <div className="sp-riepilogo">
            <div className="sp-stat-card">
              <span className="sp-stat-label">Categoria</span>
              <span className="sp-stat-text">{categoriaLabel}</span>
            </div>

            <div className="sp-stat-card">
              <span className="sp-stat-label">Giocatori</span>
              <span className="sp-stat-value">{riepilogo.totale}</span>
            </div>

            <div className="sp-stat-card">
              <span className="sp-stat-label">Media presenze</span>
              <span className="sp-stat-value">{riepilogo.media}%</span>
            </div>

            <div className="sp-stat-card">
              <span className="sp-stat-label">Sopra 80%</span>
              <span className="sp-stat-value">{riepilogo.sopra80}</span>
            </div>

            <div className="sp-stat-card">
              <span className="sp-stat-label">Sotto 50%</span>
              <span className="sp-stat-value">{riepilogo.sotto50}</span>
            </div>
          </div>

          <div className="sp-card sp-risultati-card">
            <div className="sp-risultati-header">
              <div>
                <h2>Risultati</h2>
                <p>
                  {spDataInizio && spDataFine
                    ? `Periodo: dal ${formatDateIt(spDataInizio)} al ${formatDateIt(spDataFine)}`
                    : "Seleziona categoria e intervallo date"}
                </p>
              </div>

              {riepilogo.migliore && (
                <div className="sp-highlight">
                  Migliore: {riepilogo.migliore.cognome} {riepilogo.migliore.nome} —{" "}
                  {Number(riepilogo.migliore.percentualePresenza || 0).toFixed(2)}%
                </div>
              )}
            </div>

            {spLoadingPresenze ? (
              <div className="sp-state-box">Caricamento statistiche presenze...</div>
            ) : spPresenzeStats.length === 0 ? (
              <div className="sp-state-box">
                Nessuna presenza disponibile per i filtri selezionati.
              </div>
            ) : (
              <div className="sp-list">
                {spPresenzeStats.map((stat) => {
                  const percentuale = Number(stat.percentualePresenza || 0);
                  return (
                    <div className="sp-player-card" key={stat.giocatoreId}>
                      <div className="sp-player-main">
                        <h3>
                          {stat.cognome} {stat.nome}
                        </h3>
                        <div className="sp-player-meta">
                          <span>Categoria: {stat.categoria || "-"}</span>
                        </div>
                      </div>

                      <div className="sp-player-side">
                        <span className={`sp-badge ${getBadgeClass(percentuale)}`}>
                          {percentuale.toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="form-buttons-bottom">
            <button
              className="btn-indietro"
              onClick={() => navigate("/gestionale")}
              type="button"
            >
              Torna al gestionale
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDateIt(value) {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

function getBadgeClass(percentuale) {
  if (percentuale >= 80) return "alta";
  if (percentuale >= 50) return "media";
  return "bassa";
}